import { useState } from 'react'
import { toast } from 'sonner'
import { RotateCcw, AlertTriangle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Input } from '@/components/ui/input'
import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useRebookShipment } from '@/api/hooks'
import type { components } from '@/api/schema'
import { formatApiErrorMessage } from '@/lib/api-error'
import { ShipmentCustomerNotificationPolicyFields } from '@/components/shipment-customer-notification-policy'
import { shipmentCustomerNotificationPolicy } from '@/lib/shipment-customer-notification-policy'

type Shipment = components['schemas']['PurchaseOrderShipmentRead']
type RebookBody = components['schemas']['RebookShipmentRequest']

interface RebookShipmentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  poUuid: string
  shipment: Shipment
}

/** Initial form state cloned from the source shipment.
 *
 * Strings are coerced to '' so the controlled inputs always have a defined
 * value. Booleans default to `false` if the source had `null`/`undefined`.
 */
function initialFormFromShipment(s: Shipment) {
  return {
    ship_from_name: s.ship_from_name ?? '',
    ship_from_contact_name: s.ship_from_contact_name ?? '',
    ship_from_email: s.ship_from_email ?? '',
    ship_from_phone: s.ship_from_phone ?? '',
    ship_from_address1: s.ship_from_address1 ?? '',
    ship_from_address2: s.ship_from_address2 ?? '',
    ship_from_city: s.ship_from_city ?? '',
    ship_from_state: s.ship_from_state ?? '',
    ship_from_zip: s.ship_from_zip ?? '',
    pickup_date: s.pickup_date ?? '',
    pickup_time_start: s.pickup_time_start ?? '',
    pickup_time_end: s.pickup_time_end ?? '',
    total_weight_lbs: s.total_weight_lbs != null ? String(s.total_weight_lbs) : '',
    pallet_count: s.pallet_count != null ? String(s.pallet_count) : '',
    // 'auto' encodes the server's NULL ("decide by weight") since the form
    // can't natively model tri-state nullable.
    freight_mode: (s.freight_mode ?? 'auto') as 'auto' | 'parcel' | 'ltl',
    shipping_notes: s.shipping_notes ?? '',
    liftgate_pickup: s.liftgate_pickup ?? false,
    inside_pickup: s.inside_pickup ?? false,
    residential_pickup: s.residential_pickup ?? false,
    liftgate_required: s.liftgate_required ?? false,
    appointment_required: s.appointment_required ?? false,
    inside_delivery: s.inside_delivery ?? false,
    residential_delivery: s.residential_delivery ?? false,
    notify_before_delivery: s.notify_before_delivery ?? false,
  }
}

type FormState = ReturnType<typeof initialFormFromShipment>

/** Build the request body. We send only fields that diverge from the source so
 * the backend's `model_dump(exclude_unset=True)` semantics stay clean — fields
 * not in the body are cloned from the source on the server.
 */
function buildBody(form: FormState, source: Shipment): RebookBody {
  const body: RebookBody = {}

  // Strings: empty form value with non-empty source means "user cleared it"
  const stringFields: Array<[keyof FormState, keyof RebookBody]> = [
    ['ship_from_name', 'ship_from_name'],
    ['ship_from_contact_name', 'ship_from_contact_name'],
    ['ship_from_email', 'ship_from_email'],
    ['ship_from_phone', 'ship_from_phone'],
    ['ship_from_address1', 'ship_from_address1'],
    ['ship_from_address2', 'ship_from_address2'],
    ['ship_from_city', 'ship_from_city'],
    ['ship_from_state', 'ship_from_state'],
    ['ship_from_zip', 'ship_from_zip'],
    ['pickup_time_start', 'pickup_time_start'],
    ['pickup_time_end', 'pickup_time_end'],
    ['shipping_notes', 'shipping_notes'],
  ]
  for (const [formKey, bodyKey] of stringFields) {
    const newValue = form[formKey] as string
    const oldValue = (source[bodyKey as keyof Shipment] ?? '') as string
    if (newValue !== oldValue) {
      // assigning a string-typed field to body
      ;(body as Record<string, unknown>)[bodyKey] = newValue || null
    }
  }

  // pickup_date is also a string but typed as `string` (date-only)
  if (form.pickup_date !== (source.pickup_date ?? '')) {
    body.pickup_date = form.pickup_date || null
  }

  // Numeric fields
  const oldWeight = source.total_weight_lbs != null ? String(source.total_weight_lbs) : ''
  if (form.total_weight_lbs !== oldWeight) {
    body.total_weight_lbs = form.total_weight_lbs ? form.total_weight_lbs : null
  }
  const oldPallets = source.pallet_count != null ? String(source.pallet_count) : ''
  if (form.pallet_count !== oldPallets) {
    body.pallet_count = form.pallet_count ? Number(form.pallet_count) : null
  }

  // Freight mode override — reify 'auto' as null for the request body.
  const oldMode = (source.freight_mode ?? 'auto') as 'auto' | 'parcel' | 'ltl'
  if (form.freight_mode !== oldMode) {
    body.freight_mode = form.freight_mode === 'auto' ? null : form.freight_mode
  }

  // Booleans
  const boolFields: Array<keyof RebookBody & keyof FormState> = [
    'liftgate_pickup',
    'inside_pickup',
    'residential_pickup',
    'liftgate_required',
    'appointment_required',
    'inside_delivery',
    'residential_delivery',
    'notify_before_delivery',
  ]
  for (const k of boolFields) {
    if (form[k] !== (source[k as keyof Shipment] ?? false)) {
      ;(body as Record<string, unknown>)[k] = form[k]
    }
  }

  return body
}

export function RebookShipmentSheet({
  open,
  onOpenChange,
  poUuid,
  shipment,
}: RebookShipmentSheetProps) {
  const rebookMutation = useRebookShipment(poUuid)
  const [form, setForm] = useState<FormState>(() => initialFormFromShipment(shipment))
  const sourceCustomerEmailPolicy = shipmentCustomerNotificationPolicy(
    shipment.dropship_accessorial_snapshot,
    shipment.dropship_accessorial_override,
  )
  const [customerEmailPolicy, setCustomerEmailPolicy] = useState(sourceCustomerEmailPolicy)

  const handleSubmit = () => {
    const body = buildBody(form, shipment)
    if (customerEmailPolicy.receiving_method !== sourceCustomerEmailPolicy.receiving_method) {
      body.receiving_method = customerEmailPolicy.receiving_method || null
    }
    if (customerEmailPolicy.damage_policy !== sourceCustomerEmailPolicy.damage_policy) {
      body.damage_policy = customerEmailPolicy.damage_policy || null
    }
    if (customerEmailPolicy.detention_policy !== sourceCustomerEmailPolicy.detention_policy) {
      body.detention_policy = customerEmailPolicy.detention_policy || null
    }
    if (customerEmailPolicy.detention_rate !== sourceCustomerEmailPolicy.detention_rate) {
      body.detention_rate = customerEmailPolicy.detention_rate || null
    }
    rebookMutation.mutate(
      { shipmentId: shipment.id, body },
      {
        onSuccess: () => {
          toast.success('Shipment rebooked — booking queued, label/BOL email will follow')
          onOpenChange(false)
        },
        onError: (err) => {
          toast.error(formatApiErrorMessage('Rebook failed', err))
        },
      },
    )
  }

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-4 py-4 sm:px-6">
          <SheetTitle className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            Rebook shipment
          </SheetTitle>
          <SheetDescription>
            Creates a fresh booking attempt for this shipment. The original is cancelled and a new
            pickup is queued. The label or BOL email goes out automatically once the carrier
            confirms.
          </SheetDescription>
        </SheetHeader>

        {shipment.booking_error && (
          <div className="mx-4 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/30 sm:mx-6">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <div className="min-w-0 text-xs text-red-800 dark:text-red-200">
                <p className="font-medium">Previous booking failed</p>
                <p className="mt-0.5 break-all text-red-700 dark:text-red-300">
                  {shipment.booking_error}
                </p>
              </div>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          <div className="space-y-6 px-4 py-4 sm:px-6">
            <FormSection title="Pickup address">
              <Field label="Company">
                <InlineFieldInput
                  value={form.ship_from_name}
                  onChange={(e) => setField('ship_from_name', e.target.value)}
                  placeholder="Manufacturer or warehouse name"
                />
              </Field>
              <Field label="Contact name">
                <InlineFieldInput
                  value={form.ship_from_contact_name}
                  onChange={(e) => setField('ship_from_contact_name', e.target.value)}
                  placeholder="Dock contact"
                />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Phone">
                  <InlineFieldInput
                    value={form.ship_from_phone}
                    onChange={(e) => setField('ship_from_phone', e.target.value)}
                    placeholder="555-555-5555"
                    inputMode="tel"
                  />
                </Field>
                <Field label="Email">
                  <InlineFieldInput
                    type="email"
                    value={form.ship_from_email}
                    onChange={(e) => setField('ship_from_email', e.target.value)}
                    placeholder="dock@example.com"
                  />
                </Field>
              </div>
              <Field label="Address line 1">
                <InlineFieldInput
                  value={form.ship_from_address1}
                  onChange={(e) => setField('ship_from_address1', e.target.value)}
                />
              </Field>
              <Field label="Address line 2">
                <InlineFieldInput
                  value={form.ship_from_address2}
                  onChange={(e) => setField('ship_from_address2', e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_120px_140px]">
                <Field label="City">
                  <InlineFieldInput
                    value={form.ship_from_city}
                    onChange={(e) => setField('ship_from_city', e.target.value)}
                  />
                </Field>
                <Field label="State">
                  <InlineFieldInput
                    value={form.ship_from_state}
                    onChange={(e) => setField('ship_from_state', e.target.value)}
                    maxLength={2}
                  />
                </Field>
                <Field label="ZIP">
                  <InlineFieldInput
                    value={form.ship_from_zip}
                    onChange={(e) => setField('ship_from_zip', e.target.value)}
                    inputMode="numeric"
                  />
                </Field>
              </div>
            </FormSection>

            <Separator />

            <FormSection title="Pickup window">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Date">
                  <Input
                    type="date"
                    value={form.pickup_date}
                    onChange={(e) => setField('pickup_date', e.target.value)}
                  />
                </Field>
                <Field label="Open">
                  <Input
                    type="time"
                    value={form.pickup_time_start}
                    onChange={(e) => setField('pickup_time_start', e.target.value)}
                  />
                </Field>
                <Field label="Close">
                  <Input
                    type="time"
                    value={form.pickup_time_end}
                    onChange={(e) => setField('pickup_time_end', e.target.value)}
                  />
                </Field>
              </div>
            </FormSection>

            <Separator />

            <FormSection title="Cargo">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label="Freight mode"
                  hint="Overrides weight-based provider selection at booking"
                >
                  <Select
                    value={form.freight_mode}
                    onValueChange={(v) => setField('freight_mode', v as 'auto' | 'parcel' | 'ltl')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (by weight)</SelectItem>
                      <SelectItem value="parcel">Parcel (UPS/FedEx)</SelectItem>
                      <SelectItem value="ltl">LTL (pallet/skid)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Total weight (lbs)">
                  <InlineFieldInput
                    inputMode="decimal"
                    value={form.total_weight_lbs}
                    onChange={(e) => setField('total_weight_lbs', e.target.value)}
                    placeholder="e.g. 15"
                  />
                </Field>
                <Field label="Pallet count" hint="Leave blank for small parcel">
                  <InlineFieldInput
                    inputMode="numeric"
                    value={form.pallet_count}
                    onChange={(e) => setField('pallet_count', e.target.value)}
                    placeholder="0"
                  />
                </Field>
              </div>

              {shipment.handling_units && shipment.handling_units.length > 0 && (
                <div className="rounded-md border bg-muted/30 px-3 py-2.5">
                  <p className="text-xs font-medium text-muted-foreground">
                    Handling units (cloned)
                  </p>
                  <ul className="mt-1.5 space-y-1 text-xs">
                    {shipment.handling_units.map((hu) => (
                      <li key={hu.id} className="flex flex-wrap gap-x-3 gap-y-1">
                        <span className="font-medium">
                          {hu.num_units}× {hu.unit_type}
                        </span>
                        {hu.commodities?.map((c) => (
                          <span key={c.id} className="text-muted-foreground">
                            {c.description} — {Number(c.weight_lbs).toLocaleString()} lbs
                            {c.pieces > 1 ? ` (${c.pieces} pcs)` : ''}
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </FormSection>

            <Separator />

            <FormSection title="Accessorials">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <ToggleRow
                  label="Liftgate at pickup"
                  checked={form.liftgate_pickup}
                  onCheckedChange={(v) => setField('liftgate_pickup', v)}
                />
                <ToggleRow
                  label="Inside pickup"
                  checked={form.inside_pickup}
                  onCheckedChange={(v) => setField('inside_pickup', v)}
                />
                <ToggleRow
                  label="Residential pickup"
                  checked={form.residential_pickup}
                  onCheckedChange={(v) => setField('residential_pickup', v)}
                />
                <ToggleRow
                  label="Liftgate at delivery"
                  checked={form.liftgate_required}
                  onCheckedChange={(v) => setField('liftgate_required', v)}
                />
                <ToggleRow
                  label="Inside delivery"
                  checked={form.inside_delivery}
                  onCheckedChange={(v) => setField('inside_delivery', v)}
                />
                <ToggleRow
                  label="Residential delivery"
                  checked={form.residential_delivery}
                  onCheckedChange={(v) => setField('residential_delivery', v)}
                />
                <ToggleRow
                  label="Appointment required"
                  checked={form.appointment_required}
                  onCheckedChange={(v) => setField('appointment_required', v)}
                />
                <ToggleRow
                  label="Notify before delivery"
                  checked={form.notify_before_delivery}
                  onCheckedChange={(v) => setField('notify_before_delivery', v)}
                />
              </div>
              <ShipmentCustomerNotificationPolicyFields
                value={customerEmailPolicy}
                onChange={setCustomerEmailPolicy}
              />
            </FormSection>

            <Separator />

            <FormSection title="Notes">
              <InlineFieldTextarea
                rows={3}
                value={form.shipping_notes}
                onChange={(e) => setField('shipping_notes', e.target.value)}
                placeholder="Special handling, gate codes, etc."
              />
            </FormSection>
          </div>
        </ScrollArea>

        <div className="flex flex-col-reverse gap-2 border-t px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={rebookMutation.isPending}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={rebookMutation.isPending}
            loadingText="Rebooking..."
            onClick={handleSubmit}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Rebook & Book
          </LoadingButton>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2.5">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

function ToggleRow({
  label,
  checked,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 hover:bg-muted/30">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </label>
  )
}
